import { read, WorkSheet, CellObject, WorkBook } from "xlsx";
import { TemplateConfig, TypeTemplate } from "../config";
import { UtilityTemplate } from ".";
import { IColumn, IColumnInfo } from "../models";

function getCellNumber(cell: string) {
  return Number(cell.replace(/\D/g, ""));
}
function getCellColumn(cell: string) {
  return cell.replace(/[^a-zA-Z]/g, "");
}

function readWorkbook(data: ArrayBuffer) {
  const workbook = read(data);
  return workbook;
}

async function getFirstWorkSheet(workbook: WorkBook) {
  const firstSheetName = workbook.SheetNames[0];
  const firstSheet = workbook.Sheets[firstSheetName];
  return firstSheet;
}

async function getWorkbook(file: File) {
  const buffer = await file.arrayBuffer();
  const workbook = readWorkbook(buffer);
  return workbook;
}

function traverseCell(
  workSheet: WorkSheet,
  worksheetType: TypeTemplate,
  callbackFunction: Function,
  traverseType: "Header" | "All" | "Data" = "Data"
) {
  delete workSheet["!ref"];
  delete workSheet["!margins"];

  const template = (() => {
    switch (worksheetType) {
      case "Caregiver":
        return TemplateConfig.Column.caregiver;
      case "Patient":
        return TemplateConfig.Column.patient;
      default:
        return TemplateConfig.Column.insurance;
    }
  })();

  for (const cellKey in workSheet) {
    const cellNumber = getCellNumber(cellKey);
    const column = getCellColumn(cellKey) as keyof typeof template;
    const cell: CellObject = workSheet[cellKey];

    if (cellNumber === 1 && traverseType === "Data") {
      continue;
    }
    if (cellNumber === 2 && traverseType === "Header") {
      break;
    }

    callbackFunction(column, cell, cellNumber, template);
  }
}

function convertToJSON(
  workSheet: WorkSheet,
  worksheetType: TypeTemplate,
  haveToConvert: boolean
) {
  const data: any[] = [];

  traverseCell(
    workSheet,
    worksheetType,
    (
      column: string,
      cell: CellObject,
      cellNumber: number,
      template: IColumn
    ) => {
      const row: { [key: string]: any } = data[cellNumber - 2] || {};
      if (!Object.keys(row).length) {
        data.push(row);
      }
      if (haveToConvert) {
        row[template[column].key] = UtilityTemplate.convertValue({
          cellValue: cell.v,
          cellWord: cell.w || "",
          columnValue: template[column]?.value,
          valueType: template[column].valueType,
        });
      } else {
        row[template[column].key] = cell.w;
      }
    }
  );

  return data;
}

function validateValue(workSheet: WorkSheet, worksheetType: TypeTemplate) {
  const errors: any[] = [];
  let haveError = false;
  const uniqueFields: { [key: string]: [] } = {};

  traverseCell(
    workSheet,
    worksheetType,
    (
      column: string,
      cell: CellObject,
      cellNumber: number,
      template: IColumn
    ) => {
      const row: { [key: string]: any } = errors[cellNumber - 2] || {};
      if (!Object.keys(row).length) {
        errors.push(row);
      }
      const { rowErrors, haveRowError } = UtilityTemplate.getValidationStatus({
        cellValue: cell.v,
        cellWord: cell.w || "",
        columnInfo: template[column] as IColumnInfo,
        uniqueFields,
      });
      row[template[column]?.key] = rowErrors;
      if (haveRowError) {
        haveError = true;
      }
    }
  );

  return { errors, haveError };
}

function validateTemplateType(
  workSheet: WorkSheet,
  worksheetType: TypeTemplate
) {
  let haveCorrectColumn = true;
  const columns: { [key: string]: string } = {};
  const template = (() => {
    switch (worksheetType) {
      case "Caregiver":
        return TemplateConfig.Column.caregiver;
      case "Patient":
        return TemplateConfig.Column.patient;
      default:
        return TemplateConfig.Column.insurance;
    }
  })();

  traverseCell(
    workSheet,
    worksheetType,
    (column: string, cell: CellObject) => {
      columns[column] = cell.h!;
    },
    "Header"
  );

  for (const key in template) {
    const templateColumn = template[key];
    if (templateColumn.columnText !== columns[key]) {
      haveCorrectColumn = false;
    }
  }

  return haveCorrectColumn;
}

export const UtilityWorkBook = {
  readWorkbook,
  getFirstWorkSheet,
  convertToJSON,
  getWorkbook,
  validateValue,
  validateTemplateType,
};
